import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/features/admin/api/adminApi';
import { Button } from '@/shared/components/Button';
import { Spinner } from '@/shared/components/Spinner';
import { ApiError } from '@/shared/api/httpClient';
import type { EventDto, EventStatus } from '@/shared/types/domain';

const STATUSES: EventStatus[] = ['Draft', 'Published', 'Live', 'Completed', 'Cancelled', 'Archived'];

export function EventEditPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    adminApi.events.getById(eventId).then(setEvent);
  }, [eventId]);

  if (!event) {
    return <Spinner label="Loading event" />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!event || !eventId) return;
    setError(null);
    setIsSaving(true);

    try {
      const updated = await adminApi.events.update(eventId, {
        name: event.name,
        venue: event.venue,
        startDateUtc: event.startDateUtc,
        endDateUtc: event.endDateUtc,
        status: event.status,
        duplicateScanWindowSeconds: event.duplicateScanWindowSeconds
      });
      setEvent(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <button
        type="button"
        onClick={() => navigate('/admin/events')}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', marginBottom: 'var(--space-4)', cursor: 'pointer' }}
      >
        ← All events
      </button>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error && <div className="error-banner">{error}</div>}

        <div className="field">
          <label className="field__label">Name</label>
          <input
            className="field__input"
            value={event.name}
            onChange={(e) => setEvent({ ...event, name: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="field__label">Venue</label>
          <input
            className="field__input"
            value={event.venue}
            onChange={(e) => setEvent({ ...event, venue: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="field__label">Status</label>
          <select
            className="field__input"
            value={event.status}
            onChange={(e) => setEvent({ ...event, status: e.target.value as EventStatus })}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label">Duplicate scan window (seconds)</label>
          <input
            type="number"
            className="field__input"
            min={0}
            value={event.duplicateScanWindowSeconds}
            onChange={(e) => setEvent({ ...event, duplicateScanWindowSeconds: Number(e.target.value) })}
          />
        </div>

        <Button type="submit" isLoading={isSaving} style={{ alignSelf: 'flex-start' }}>
          Save changes
        </Button>
      </form>
    </div>
  );
}
